import { ElementType, FC, FormEvent, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Divider,
  Grid,
  IconButtonProps,
  Paper,
  Stack,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled, TypographyVariant } from '@mui/material/styles';

import { HasPermission } from '@components/auth';
import { FilterGroup } from '@models/SearchFilter';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { getFilterCount } from '@modules/layouts/hooks/useSearchAndFilter/utils';

import DistinctBy from '../DistinctBy';
import FilterDisplay from '../FilterDisplay';
import SearchFilterButton from '../SearchFilterButton';
import SearchToolbarProviderSelector from '../SearchToolbarProviderSelector';

import ConfigureSearch from './ConfigureSearch';
import SearchField from './SearchField';
import SearchToolbarActions from './SearchToolbarActions';

const ToolbarWrapper = styled(Stack)`
  overflow: hidden;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    align-items: center;
    border-radius: 9999px;
    padding-left: ${({ theme }) => theme.spacing(0.5)};
  }
` as typeof Stack;

type SearchDividerProps = { vertical?: boolean };

const SearchDivider: FC<SearchDividerProps> = ({ vertical }) => (
  <Divider
    sx={{
      borderColor: theme =>
        theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.divider,
    }}
    {...(vertical ? { flexItem: true, orientation: 'vertical', variant: 'fullWidth' } : {})}
  />
);

export type SearchToolbarProps = {
  disableActions?: boolean;
  disableHeading?: boolean;
  disableProviderSelector?: boolean;
  disableSaveSearch?: boolean;
  headingProps?: TypographyProps;
};

const SearchToolbar: FC<SearchToolbarProps> = ({
  disableActions,
  disableHeading,
  disableProviderSelector,
  disableSaveSearch,
  headingProps: {
    component: headingComponent = 'h1',
    variant: headingVariant = 'h2',
    ...HeadingProps
  } = {},
}) => {
  const { t } = useTranslation();

  const {
    pageNo,
    searchQuery: { filter: initialFilter, indexName, queryText: initialQueryText },
    updateFilter,
    updateQueryText,
  } = useSearchAndFilter();

  const [filter, setFilter] = useState<FilterGroup>(initialFilter);
  const [queryText, setQueryText] = useState(initialQueryText);

  useEffect(() => setQueryText(initialQueryText), [initialQueryText]);

  useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget.querySelector('input:focus') as HTMLInputElement)?.blur();
    updateQueryText(queryText);
  };

  const handleUpdateFullQuery = (newFilters: FilterGroup) => updateFilter(newFilters, queryText);

  const handleUpdateFilters = (newFilters: FilterGroup) => updateFilter(newFilters);

  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('md'));
  const mdScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const iconButtonSize: IconButtonProps['size'] = smScreen
    ? 'small'
    : mdScreen
      ? 'medium'
      : 'large';

  const headingRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    headingRef.current?.scrollIntoView({ block: 'center' });
  }, [pageNo]);

  return (
    <Fragment>
      <Grid container spacing={1}>
        {!disableHeading && (
          <Grid size='grow'>
            <Stack alignItems='center' direction='row' spacing={1}>
              <Typography
                component={headingComponent as ElementType}
                display='inline'
                ref={headingRef}
                variant={headingVariant as TypographyVariant}
                {...HeadingProps}
              >
                {t('search.heading', { context: indexName })}
              </Typography>
              <ConfigureSearch />
            </Stack>
          </Grid>
        )}

        <HasPermission>
          {!disableActions && (
            <SearchToolbarActions disableSaveSearch={disableSaveSearch} filter={filter} />
          )}
        </HasPermission>

        <Grid size={12}>
          <ToolbarWrapper component={Paper} direction={{ xs: 'column', md: 'row' }}>
            <Box flex={1} pl={2}>
              <SearchField
                handleSubmit={handleSubmit}
                iconButtonSize={iconButtonSize}
                queryText={queryText}
                setQueryText={setQueryText}
              />
            </Box>

            <SearchDivider vertical={!smScreen} />

            <Box flex={{ sm: 0.85, lg: 0.75, xl: 0.5 }}>
              <SearchFilterButton updateFilter={handleUpdateFullQuery} />
            </Box>

            {!disableProviderSelector && (
              <Fragment>
                <SearchDivider vertical={!smScreen} />

                <Box flex={{ sm: 0.85, lg: 0.75, xl: 0.5 }}>
                  <SearchToolbarProviderSelector />
                </Box>
              </Fragment>
            )}

            <SearchDivider vertical={!smScreen} />

            <Box flex={{ sm: 0.85, lg: 0.75, xl: 0.5 }}>
              <DistinctBy />
            </Box>
          </ToolbarWrapper>
        </Grid>

        {getFilterCount(filter) > 0 && (
          <Grid size='grow'>
            <FilterDisplay filterGroup={filter} onChange={handleUpdateFilters} />
          </Grid>
        )}
      </Grid>
    </Fragment>
  );
};

export default SearchToolbar;
